// SF-174 Component 6: `computeDefenseLeftAtHome()` — mirrors `MilitarySystem.computeDefense()`'s
// *documented, intended* formula (MilitarySystem.java:487-489/501-510: "Raw Defense = (Defense
// Specs × Def Spec Points) + (Elites at Home × Elite's Defense) + (Soldiers × Def Points Sold ×
// Aggression) + Townwatch"), grounded against MilitaryMath.computeModifiedDefense/
// MilitarySystem.computeRawDefense/computeDefense (MilitarySystem.java:490-527).
//
// SF-183 Component 7: this is a *forecast* helper, not a duplicate of any backend value — it
// subtracts the composition currently being drafted in the War Room from the province's at-home
// stock, to answer "what will my defense be if I send this," a question with no backend
// equivalent (the backend only ever reports *current* state). Its two inputs now read the real
// at-home DTO fields (`soldiersAtHome`/`elitesAtHome`, Component 6) instead of manually
// approximating at-home stock from the away-inclusive `soldiers`/`elites` fields — the prior
// approximation is superseded, not this helper's forecast role itself.
//
// (The dragon-effect omission this comment block used to document as a live gap was already
// closed by SF-180's dragon-modifier threading in ProvinceDtoMapper.java, unrelated to SF-183.)

import { RACE_UNIT_STATS } from '../constants/game';
import type { Province } from '../types/province';

export interface HomeDefenseComposition {
  soldiers: number;
  elites: number;
  /**
   * SF-183: optional — not read by `computeDefenseLeftAtHome`'s own formula (offense specialists
   * contribute 0 raw defense under the normal, non-Ambush chain), but `DefenseLeftAtHomeCard` reads
   * it to check the Ambush-floorless-divergence caption's "all four COMBAT_UNITS at 0 at home"
   * condition. Real callers (the War Room shells) already pass a full `ArmyComposition`, which has
   * this field structurally.
   */
  offenseSpecialists?: number;
}

export interface DefenseLeftAtHomeResult {
  defenseLeftAtHome: number;
  dpaLeftAtHome: number;
}

export function computeDefenseLeftAtHome(
  province: Province,
  composition: HomeDefenseComposition
): DefenseLeftAtHomeResult {
  const raceStats = RACE_UNIT_STATS[province.metadata.race.toUpperCase()] ?? RACE_UNIT_STATS.HUMAN;

  const soldiersAtHome = Math.max(
    0,
    (province.population.soldiersAtHome ?? province.population.soldiers) - composition.soldiers
  );
  const elitesAtHome = Math.max(
    0,
    (province.population.elitesAtHome ?? province.population.elites) - composition.elites
  );

  const hasAggression = province.magic.activeEffects.some((e) => e.effectType === 'AGGRESSION');
  const soldierDefPts = raceStats.soldier.def - (hasAggression ? 1 : 0);

  const hasTownwatch = province.magic.activeEffects.some((e) => e.effectType === 'TOWNWATCH');
  const townwatchPts = hasTownwatch ? Math.round(province.population.peasants / 4) : 0;

  const rawDefenseRemaining =
    soldiersAtHome * soldierDefPts +
    elitesAtHome * raceStats.elite.def +
    province.population.defenseSpecialists * raceStats.defense.def +
    townwatchPts;

  // Explicit === true guard (Phase 3, Data Model Changes) — undefined-safe against a stale
  // pre-deploy cache entry missing the new optional field.
  const monarchBonusActive =
    province.metadata.isMonarch && province.kingdom.hasHostileOrWarRelationship === true;
  const monarchBonus = monarchBonusActive ? 1.1 : 1.0; // Property.MONARCH_MILITARY_DEFENSE_MOD default

  const acres = province.territory.totals.acres;
  const defenseLeftAtHome = Math.round(
    Math.max(rawDefenseRemaining * province.military.defensiveME, acres) * monarchBonus
  );
  const dpaLeftAtHome = acres > 0 ? defenseLeftAtHome / acres : 0;

  return { defenseLeftAtHome, dpaLeftAtHome };
}
